import jsPDF from 'jspdf';
import { DeclarationData } from './formTypes';

/**
 * Generate PDF from health declaration form data
 */
export const generateHealthDeclarationPDF = (data: DeclarationData) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Set RTL direction
  pdf.setLanguage('he');

  // Title
  pdf.setFont('Courier', 'bold');
  pdf.setFontSize(18);
  pdf.text('הצהרת בריאות', 105, 20, { align: 'center' });

  let yPosition = 35;
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const maxWidth = 180;

  // Helper function to add text with wrapping
  const addText = (text: string, isBold = false) => {
    if (yPosition > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
    }
    pdf.setFont('Courier', isBold ? 'bold' : 'normal');
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(text, maxWidth);
    pdf.text(lines, margin, yPosition, { align: 'right' });
    yPosition += lines.length * 5 + 2;
  };

  // Personal Info Section
  addText('פרטים אישיים', true);
  addText(`שם: ${data.firstName} ${data.lastName}`);
  addText(`תאריך לידה: ${data.dateOfBirth}`);
  addText(`טלפון: ${data.phone}`);
  addText(`מייל: ${data.email}`);
  if (data.occupation) {
    addText(`עיסוק: ${data.occupation}`);
  }

  yPosition += 5;

  // Medical Info Section
  addText('מידע רפואי', true);
  addText(`מגבלה לפעילות גופנית: ${data.physicalLimitation ? 'כן' : 'לא'}`);
  if (data.physicalLimitationDetails) {
    addText(`פרטים: ${data.physicalLimitationDetails}`);
  }

  addText(`לחץ דם: ${data.bloodPressure || 'לא צוין'}`);
  if (data.bloodPressure && data.bloodPressure !== 'normal') {
    addText(`מאוזן תרופתית: ${data.bloodPressureManaged ? 'כן' : 'לא'}`);
  }

  addText(`ניתוחים: ${data.surgeries ? 'כן' : 'לא'}`);
  if (data.surgeriesDetails) {
    addText(`פרטים: ${data.surgeriesDetails}`);
  }

  addText(`אירועי לב: ${data.heartEvents ? 'כן' : 'לא'}`);
  if (data.heartEventsDetails) {
    addText(`פרטים: ${data.heartEventsDetails}`);
  }

  addText(`פציעות: ${data.injuries ? 'כן' : 'לא'}`);
  if (data.injuriesDetails) {
    addText(`פרטים: ${data.injuriesDetails}`);
  }

  addText(`דלדול עצם: ${data.osteoporosis ? 'כן' : 'לא'}`);
  addText(`סכרת: ${data.arthritis ? 'כן' : 'לא'}`);
  if (data.arthritis) {
    addText(`מאוזנת תרופתית: ${data.arthritisManaged ? 'כן' : 'לא'}`);
  }

  addText(`תרופות: ${data.medications ? 'כן' : 'לא'}`);
  if (data.medicationsDetails) {
    addText(`פרטים: ${data.medicationsDetails}`);
  }

  addText(`עישון: ${data.smoking ? 'כן' : 'לא'}`);
  if (data.smokingDetails) {
    addText(`פרטים: ${data.smokingDetails}`);
  }

  if (data.pregnancy) {
    addText(`הריון: כן - שבוע ${data.pregnancyWeek}`);
  }

  if (data.additionalInfo) {
    addText(`מידע נוסף: ${data.additionalInfo}`);
  }

  yPosition += 5;

  // Declaration Section
  addText('הצהרה', true);
  const declarationText =
    'הנני מצהיר/ה ומתחייב/ת כי אני יכול/ה לעסוק בפעילות גופנית בהתאם למגבלות הרפואיות הידועות לי ואין המאמן/ת אחראי/ת לכל אובדן או נזק גופני העלולים להיגרם לי בתחומם. הנני מצהיר/ה כי קראתי, הבנתי ומסכים/ה לכל האמור לעיל, ושכל הפרטים נכונים.';
  addText(declarationText);

  yPosition += 5;

  // Signature Section
  addText('חתימה וחתימה דיגיטלית', true);

  // Add signature image if available
  if (data.signature) {
    try {
      if (yPosition > pageHeight - 80) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.addImage(data.signature, 'PNG', margin + 40, yPosition, 100, 50);
      yPosition += 55;
    } catch (error) {
      console.error('Error adding signature image:', error);
    }
  }

  // Add date
  addText(`תאריך: ${data.declarationDate}`);

  // Footer
  yPosition = pageHeight - 15;
  pdf.setFont('Courier', 'normal');
  pdf.setFontSize(8);
  pdf.text(
    `מסמך זה נוצר ב-${new Date().toLocaleDateString('he-IL')}`,
    105,
    yPosition,
    { align: 'center' }
  );

  return pdf;
};

/**
 * Download PDF file
 */
export const downloadHealthDeclarationPDF = (data: DeclarationData) => {
  const pdf = generateHealthDeclarationPDF(data);
  const fileName = `health_declaration_${data.firstName}_${data.lastName}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);
};
